import React, { useState, useEffect } from 'react';
import { Card, CardBody, Typography, Input, Button, Select, Option, Dialog, DialogHeader, DialogBody, DialogFooter } from "@material-tailwind/react";
import axios from 'axios';
import { FaPlus, FaSearch, FaEdit, FaUpload, FaCheckCircle, FaDownload } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
const { rootUrl } = require('../../../config/config');

// Every fleet car has been driven; lower values are placeholders, not readings
const MIN_MILEAGE_KM = 100;
const todayInNairobi = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Nairobi' });

const GovernmentInspections = () => {
  const [inspections, setInspections] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadLink, setUploadLink] = useState('');
  const [uploadLinkType, setUploadLinkType] = useState('certificate'); // 'certificate' or 'report'
  const [isReplacingLink, setIsReplacingLink] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [modalError, setModalError] = useState('');
  const [editModalError, setEditModalError] = useState('');
  const [uploadModalError, setUploadModalError] = useState('');
  const [isVerifyingUrl, setIsVerifyingUrl] = useState(false);
  const [createForm, setCreateForm] = useState({
    plate_no: '',
    inspection_date: todayInNairobi(),
    certificate_number: '',
    certificate_link: '',
    inspection_report_link: '',
    notes: '',
    inspector_name: '',
    inspector_organization: 'NTSA',
    result: 'passed',
    mileage: '',
    input_by: ''
  });
  const [editForm, setEditForm] = useState({
    certificate_number: '',
    certificate_link: '',
    inspection_report_link: '',
    notes: '',
    inspector_name: '',
    inspector_organization: '',
    result: 'passed',
    mileage: '',
    inspection_status: ''
  });
  const { user } = useAuth();

  const findCar = (plateNo) => cars.find((car) => car.plate_no === plateNo);

  const formatMileageHint = (plateNo) => {
    const car = findCar(plateNo);
    if (!car) return null;
    if (car.current_mileage == null) return 'No mileage on record for this vehicle';
    const date = car.last_mileage_date
      ? new Date(car.last_mileage_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : 'unknown date';
    return `Last recorded: ${Number(car.current_mileage).toLocaleString()} km on ${date}`;
  };

  // checkBackwards is off when editing, since the car's last reading may be this inspection
  const getMileageError = (plateNo, value, checkBackwards) => {
    if (value === '' || value === null || value === undefined) return '';
    const km = parseInt(value, 10);
    if (Number.isNaN(km) || km < MIN_MILEAGE_KM) {
      return `Enter the real odometer reading (at least ${MIN_MILEAGE_KM} km).`;
    }
    const car = findCar(plateNo);
    if (checkBackwards && car?.current_mileage != null && km < Number(car.current_mileage)) {
      return `Mileage cannot go backwards. Last recorded was ${Number(car.current_mileage).toLocaleString()} km.`;
    }
    return '';
  };

  const createMileageError = getMileageError(createForm.plate_no, createForm.mileage, true);
  const editMileageError = getMileageError(selectedInspection?.plate_no, editForm.mileage, false);

  // Fetch all government inspections
  const fetchInspections = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || 'default-token';
      const response = await axios.get(`${rootUrl}/cars/inspections/government`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInspections(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch inspections.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch cars for dropdown
  const fetchCars = async () => {
    try {
      const token = localStorage.getItem('authToken') || 'default-token';
      const response = await axios.get(`${rootUrl}/cars`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCars(response.data || []);
    } catch (err) {
      console.error('Failed to fetch cars:', err);
    }
  };

  useEffect(() => {
    fetchInspections();
    fetchCars();
    if (user?.username) {
      setCreateForm(prev => ({ ...prev, input_by: user.username }));
    }
  }, [user]);

  // Create new inspection
  const handleCreateInspection = async () => {
    setModalError('');
    setError('');
    setSuccess('');

    if (!createForm.plate_no || !createForm.inspection_date || !createForm.certificate_number || !createForm.notes || !createForm.mileage) {
      setModalError('Please fill all required fields.');
      return;
    }

    if (createForm.inspection_date > todayInNairobi()) {
      setModalError('Inspection date cannot be in the future. Record the inspection on the day it happens.');
      return;
    }

    if (createMileageError) {
      setModalError(createMileageError);
      return;
    }

    // Verify URLs if provided
    if (createForm.certificate_link) {
      const certVerification = await verifyUrlExists(createForm.certificate_link);
      if (!certVerification.valid) {
        setModalError(`Certificate link is invalid: ${certVerification.message}`);
        return;
      }
    }

    if (createForm.inspection_report_link) {
      const reportVerification = await verifyUrlExists(createForm.inspection_report_link);
      if (!reportVerification.valid) {
        setModalError(`Report link is invalid: ${reportVerification.message}`);
        return;
      }
    }

    try {
      const token = localStorage.getItem('authToken') || 'default-token';
      await axios.post(
        `${rootUrl}/cars/inspections/government`,
        createForm,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccess('Government inspection created successfully!');
      setModalError('');
      setShowCreateModal(false);
      setCreateForm({
        plate_no: '',
        inspection_date: todayInNairobi(),
        certificate_number: '',
        certificate_link: '',
        inspection_report_link: '',
        notes: '',
        inspector_name: '',
        inspector_organization: 'NTSA',
        result: 'passed',
        mileage: '',
        input_by: user?.username || ''
      });
      fetchInspections();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to create inspection.');
      console.error(err);
    }
  };

  // Load inspection for editing
  const loadInspectionForEdit = (inspection) => {
    setEditForm({
      certificate_number: inspection.certificate_number || '',
      certificate_link: inspection.certificate_link || '',
      inspection_report_link: inspection.inspection_report_link || '',
      notes: inspection.notes || '',
      inspector_name: inspection.inspector_name || '',
      inspector_organization: inspection.inspector_organization || 'NTSA',
      result: inspection.result || 'passed',
      mileage: inspection.mileage || '',
      inspection_status: inspection.inspection_status || 'in_progress'
    });
    setSelectedInspection(inspection);
    setShowEditModal(true);
  };

  // Update inspection
  const handleUpdateInspection = async () => {
    setEditModalError('');
    setError('');
    setSuccess('');

    if (!editForm.certificate_number || !editForm.notes || !editForm.mileage) {
      setEditModalError('Please fill all required fields.');
      return;
    }

    if (editMileageError) {
      setEditModalError(editMileageError);
      return;
    }

    // Verify URLs if provided
    if (editForm.certificate_link) {
      const certVerification = await verifyUrlExists(editForm.certificate_link);
      if (!certVerification.valid) {
        setEditModalError(`Certificate link is invalid: ${certVerification.message}`);
        return;
      }
    }

    if (editForm.inspection_report_link) {
      const reportVerification = await verifyUrlExists(editForm.inspection_report_link);
      if (!reportVerification.valid) {
        setEditModalError(`Report link is invalid: ${reportVerification.message}`);
        return;
      }
    }

    try {
      const token = localStorage.getItem('authToken') || 'default-token';
      await axios.put(
        `${rootUrl}/cars/inspections/government/${selectedInspection.id}`,
        {
          ...editForm,
          edit_by: user?.username || 'system'
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccess('Government inspection updated successfully!');
      setEditModalError('');
      setShowEditModal(false);
      setSelectedInspection(null);
      fetchInspections();
    } catch (err) {
      setEditModalError(err.response?.data?.error || 'Failed to update inspection.');
      console.error(err);
    }
  };

  // Handle link upload
  const handleUploadLink = async () => {
    setUploadModalError('');

    if (!uploadLink.trim()) {
      setUploadModalError('Please enter a link.');
      return;
    }

    if (!selectedInspection) {
      setUploadModalError('No inspection selected.');
      return;
    }

    // Verify URL before saving
    setIsVerifyingUrl(true);
    const verification = await verifyUrlExists(uploadLink.trim());
    setIsVerifyingUrl(false);

    if (!verification.valid) {
      setUploadModalError(verification.message || 'Invalid URL. Please check the link and try again.');
      return;
    }

    try {
      const token = localStorage.getItem('authToken') || 'default-token';
      const updatePayload = {
        edit_by: user?.username || 'system'
      };

      if (uploadLinkType === 'certificate') {
        updatePayload.certificate_link = uploadLink.trim();
      } else {
        updatePayload.inspection_report_link = uploadLink.trim();
      }

      await axios.put(
        `${rootUrl}/cars/inspections/government/${selectedInspection.id}`,
        updatePayload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccess('Link updated successfully!');
      setUploadModalError('');
      setShowUploadModal(false);
      setUploadLink('');
      setSelectedInspection(null);
      setIsReplacingLink(false);
      fetchInspections();
    } catch (err) {
      setUploadModalError(err.response?.data?.error || 'Failed to update link.');
      console.error(err);
    }
  };

  // Verify URL using backend validator
  const verifyUrlExists = async (url) => {
    try {
      if (!url || !url.trim()) {
        return { valid: false, message: 'URL is required' };
      }

      const token = localStorage.getItem('authToken') || 'default-token';
      const response = await axios.post(
        `${rootUrl}/cars/inspections/verify-url`,
        { url: url.trim() },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return {
        valid: response.data.valid,
        message: response.data.message || response.data.error,
        warning: response.data.warning || false
      };
    } catch (err) {
      console.error('Error verifying URL:', err);
      return { 
        valid: false, 
        message: err.response?.data?.error || 'Error verifying URL. Please check the link and try again.' 
      };
    }
  };

  // Complete inspection
  const handleCompleteInspection = async (inspection) => {
    // Check if certificate link exists
    if (!inspection.certificate_link || !inspection.certificate_link.trim()) {
      setError('Cannot complete inspection: Please add the certificate link first.');
      return;
    }

    // Verify certificate link is valid
    setError('Verifying certificate link...');
    const urlVerification = await verifyUrlExists(inspection.certificate_link);
    
    if (!urlVerification.valid) {
      setError(`Cannot complete inspection: ${urlVerification.message}. Please verify the certificate link is correct.`);
      return;
    }

    if (!window.confirm('Are you sure you want to mark this inspection as completed?')) {
      setError('');
      return;
    }

    try {
      const token = localStorage.getItem('authToken') || 'default-token';
      
      await axios.put(
        `${rootUrl}/cars/inspections/government/${inspection.id}`,
        {
          inspection_status: 'completed',
          edit_by: user?.username || 'system'
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccess('Inspection marked as completed!');
      setError('');
      fetchInspections();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete inspection.');
      console.error(err);
    }
  };

  // Filter and sort inspections
  const filteredInspections = inspections
    .filter(inspection => {
    const searchLower = searchTerm.toLowerCase();
    return (
      inspection.plate_no?.toLowerCase().includes(searchLower) ||
      inspection.certificate_number?.toLowerCase().includes(searchLower) ||
        inspection.inspector_name?.toLowerCase().includes(searchLower) ||
        inspection.inspector_organization?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      // Sort by input_date descending (newest first)
      const dateA = a.input_date ? new Date(a.input_date).getTime() : 0;
      const dateB = b.input_date ? new Date(b.input_date).getTime() : 0;
      return dateB - dateA; // Descending order
  });

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Get status color for result column
  const getStatusColor = (result) => {
    if (!result) return 'bg-gray-100 text-gray-800';
    switch (result.toLowerCase()) {
      case 'passed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'conditional':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format result text for display
  const formatResult = (result) => {
    if (!result) return 'Pending';
    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  // Get inspection status badge
  const getInspectionStatus = (inspection) => {
    const status = inspection.inspection_status?.toLowerCase();
    if (status === 'completed') return { text: 'Completed', class: 'bg-green-100 text-green-800' };
    if (status === 'in_progress') return { text: 'In Progress', class: 'bg-blue-100 text-blue-800' };
    if (status === 'scheduled') return { text: 'Scheduled', class: 'bg-yellow-100 text-yellow-800' };
    return { text: 'Pending', class: 'bg-gray-100 text-gray-800' };
  };

  // Handle certificate download
  const handleDownloadCertificate = (inspection) => {
    if (inspection.certificate_link) {
      // Open certificate link in new tab
      window.open(inspection.certificate_link, '_blank', 'noopener,noreferrer');
    } else {
      setError('Certificate link not available for this inspection.');
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto mt-6 px-2 sm:px-4">
      <Card className="border-t">
        <CardBody className="p-3 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">
              Government Inspections
            </h2>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-brand-primary text-white flex items-center gap-2 shadow-none rounded-none w-full sm:w-auto text-sm sm:text-base"
            >
              <FaPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Create Inspection</span>
              <span className="sm:hidden">Create</span>
            </Button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          {/* Search */}
          <div className="mb-4 sm:mb-6 flex gap-4">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm sm:text-base" />
              <input
                type="text"
                placeholder="Search by plate number, certificate number, or inspector..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>
          </div>

          {/* Inspections Table */}
          {loading ? (
            <Typography>Loading...</Typography>
          ) : filteredInspections.length === 0 ? (
            <Typography className="text-center py-8 text-gray-500">
              {searchTerm ? 'No inspections found matching your search.' : 'No inspections found.'}
            </Typography>
          ) : (
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full table-auto border-collapse min-w-[800px]">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm min-w-[130px] w-[130px]">Date</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Plate No</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm hidden md:table-cell">Certificate #</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm hidden lg:table-cell">Inspector</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm hidden lg:table-cell">Mileage</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Status</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Result</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm hidden md:table-cell">Expiry Date</th>
                    <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInspections.map((inspection) => {
                    const statusBadge = getInspectionStatus(inspection);
                    return (
                    <tr key={inspection.id} className="hover:bg-gray-50">
                        <td className="border px-2 sm:px-4 py-2 min-w-[130px] w-[130px]">
                          <Typography className="text-xs sm:text-sm whitespace-nowrap">{formatDate(inspection.inspection_date)}</Typography>
                        </td>
                        <td className="border px-2 sm:px-4 py-2">
                          <Typography className="font-semibold text-xs sm:text-sm">{inspection.plate_no}</Typography>
                        </td>
                        <td className="border px-2 sm:px-4 py-2 hidden md:table-cell">
                          <Typography className="text-xs sm:text-sm">{inspection.certificate_number || '-'}</Typography>
                      </td>
                        <td className="border px-2 sm:px-4 py-2 hidden lg:table-cell">
                          <Typography className="text-xs sm:text-sm">{inspection.inspector_name || inspection.inspector_organization || '-'}</Typography>
                      </td>
                        <td className="border px-2 sm:px-4 py-2 hidden lg:table-cell">
                          <Typography className="text-xs sm:text-sm">{inspection.mileage ? `${inspection.mileage.toLocaleString()} km` : '-'}</Typography>
                      </td>
                        <td className="border px-2 sm:px-4 py-2">
                          <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-none text-xs font-medium ${statusBadge.class}`}>
                            {statusBadge.text}
                          </span>
                      </td>
                        <td className="border px-2 sm:px-4 py-2">
                          <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-none text-xs font-medium ${getStatusColor(inspection.result)}`}>
                            {formatResult(inspection.result)}
                        </span>
                      </td>
                        <td className="border px-2 sm:px-4 py-2 hidden md:table-cell">
                          <Typography className="text-xs sm:text-sm">{formatDate(inspection.expiry_date)}</Typography>
                        </td>
                        <td className="border px-2 sm:px-4 py-2">
                          <div className="flex flex-wrap gap-1 sm:gap-2 justify-start">
                            {/* Always show download button if certificate link exists */}
                            {inspection.certificate_link && (
                              <Button
                                onClick={() => handleDownloadCertificate(inspection)}
                                className="bg-gray-600 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs flex items-center gap-0.5 sm:gap-1 shadow-none rounded-none"
                                size="sm"
                                title="Download Certificate"
                              >
                                <FaDownload className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                                <span className="hidden sm:inline">Certificate</span>
                              </Button>
                            )}
                            {/* Only show actions if not completed */}
                            {inspection.inspection_status !== 'completed' && (
                              <>
                                <Button
                                  onClick={() => loadInspectionForEdit(inspection)}
                                  className="bg-blue-600 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs flex items-center gap-0.5 sm:gap-1 shadow-none rounded-none"
                                  size="sm"
                                >
                                  <FaEdit className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                                  <span className="hidden sm:inline">Edit</span>
                                </Button>
                                <Button
                                  onClick={() => {
                                    setSelectedInspection(inspection);
                                    setUploadLink(inspection.certificate_link || '');
                                    setUploadLinkType('certificate');
                                    setIsReplacingLink(false);
                                    setShowUploadModal(true);
                                  }}
                                  className="bg-purple-500 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs flex items-center gap-0.5 sm:gap-1 shadow-none rounded-none"
                                  size="sm"
                                >
                                  <FaUpload className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                                  <span className="hidden sm:inline">Upload</span>
                                </Button>
                                <Button
                                  onClick={() => handleCompleteInspection(inspection)}
                                  className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs flex items-center gap-0.5 sm:gap-1 shadow-none rounded-none ${
                                    !inspection.certificate_link 
                                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                                      : 'bg-green-500 text-white'
                                  }`}
                                  size="sm"
                                  disabled={!inspection.certificate_link}
                                  title={!inspection.certificate_link ? 'Add certificate link first to complete inspection' : 'Mark inspection as completed'}
                                >
                                  <FaCheckCircle className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                                  <span className="hidden sm:inline">Complete</span>
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create Inspection Modal */}
      <Dialog open={showCreateModal} handler={setShowCreateModal} size="lg" className="m-2 sm:m-4">
        <DialogHeader>Create Government Inspection</DialogHeader>
        <DialogBody>
          {modalError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {modalError}
            </div>
          )}
          <div className="space-y-4">
            <Select
              label="Plate Number *"
              value={createForm.plate_no}
              onChange={(val) => setCreateForm({ ...createForm, plate_no: val })}
            >
              {cars.map((car) => (
                <Option key={car.plate_no} value={car.plate_no}>
                  {car.plate_no}
                </Option>
              ))}
            </Select>
            <Input
              type="date"
              label="Inspection Date *"
              value={createForm.inspection_date}
              max={todayInNairobi()}
              onChange={(e) => setCreateForm({ ...createForm, inspection_date: e.target.value })}
            />
            <Input
              type="text"
              label="Certificate Number *"
              value={createForm.certificate_number}
              onChange={(e) => setCreateForm({ ...createForm, certificate_number: e.target.value })}
            />
            <Input
              type="text"
              label="Inspector Name"
              value={createForm.inspector_name}
              onChange={(e) => setCreateForm({ ...createForm, inspector_name: e.target.value })}
            />
            <Input
              type="text"
              label="Inspector Organization"
              value={createForm.inspector_organization}
              onChange={(e) => setCreateForm({ ...createForm, inspector_organization: e.target.value })}
            />
            <Select
              label="Result"
              value={createForm.result}
              onChange={(val) => setCreateForm({ ...createForm, result: val })}
            >
              <Option value="passed">Passed</Option>
              <Option value="failed">Failed</Option>
              <Option value="conditional">Conditional</Option>
            </Select>
            <Input
              type="url"
              label="Certificate Link"
              value={createForm.certificate_link}
              onChange={(e) => setCreateForm({ ...createForm, certificate_link: e.target.value })}
              placeholder="https://drive.google.com/..."
            />
            <Input
              type="url"
              label="Report Link"
              value={createForm.inspection_report_link}
              onChange={(e) => setCreateForm({ ...createForm, inspection_report_link: e.target.value })}
              placeholder="https://drive.google.com/..."
            />
            <div>
              <Input
                type="number"
                label="Mileage (km) *"
                value={createForm.mileage}
                onChange={(e) => setCreateForm({ ...createForm, mileage: e.target.value })}
                min={MIN_MILEAGE_KM}
                className="text-right"
                error={!!createMileageError}
              />
              {formatMileageHint(createForm.plate_no) && (
                <p className="text-xs text-gray-500 mt-1">{formatMileageHint(createForm.plate_no)}</p>
              )}
              {createMileageError && <p className="text-xs text-red-500 mt-1">{createMileageError}</p>}
            </div>
            <Input
              type="text"
              label="Notes *"
              value={createForm.notes}
              onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button 
            variant="text" 
            onClick={() => {
              setShowCreateModal(false);
              setModalError('');
            }} 
            className="mr-2 shadow-none"
          >
            Cancel
          </Button>
          <Button onClick={handleCreateInspection} className="bg-brand-primary shadow-none rounded-none">
            Create
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Edit Inspection Modal */}
      <Dialog open={showEditModal} handler={setShowEditModal} size="lg" className="m-2 sm:m-4">
        <DialogHeader>Edit Government Inspection</DialogHeader>
        <DialogBody>
          {editModalError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {editModalError}
            </div>
          )}
          <div className="space-y-4">
            <Input
              type="text"
              label="Certificate Number *"
              value={editForm.certificate_number}
              onChange={(e) => setEditForm({ ...editForm, certificate_number: e.target.value })}
            />
            <Input
              type="text"
              label="Inspector Name"
              value={editForm.inspector_name}
              onChange={(e) => setEditForm({ ...editForm, inspector_name: e.target.value })}
            />
            <Input
              type="text"
              label="Inspector Organization"
              value={editForm.inspector_organization}
              onChange={(e) => setEditForm({ ...editForm, inspector_organization: e.target.value })}
            />
            <Select
              label="Result"
              value={editForm.result}
              onChange={(val) => setEditForm({ ...editForm, result: val })}
            >
              <Option value="passed">Passed</Option>
              <Option value="failed">Failed</Option>
              <Option value="conditional">Conditional</Option>
            </Select>
            <Input
              type="url"
              label="Certificate Link"
              value={editForm.certificate_link}
              onChange={(e) => setEditForm({ ...editForm, certificate_link: e.target.value })}
              placeholder="https://drive.google.com/..."
            />
            <Input
              type="url"
              label="Report Link"
              value={editForm.inspection_report_link}
              onChange={(e) => setEditForm({ ...editForm, inspection_report_link: e.target.value })}
              placeholder="https://drive.google.com/..."
            />
            <div>
              <Input
                type="number"
                label="Mileage (km) *"
                value={editForm.mileage}
                onChange={(e) => setEditForm({ ...editForm, mileage: e.target.value })}
                min={MIN_MILEAGE_KM}
                className="text-right"
                error={!!editMileageError}
              />
              {formatMileageHint(selectedInspection?.plate_no) && (
                <p className="text-xs text-gray-500 mt-1">{formatMileageHint(selectedInspection?.plate_no)}</p>
              )}
              {editMileageError && <p className="text-xs text-red-500 mt-1">{editMileageError}</p>}
            </div>
            <Input
              type="text"
              label="Notes *"
              value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
            />
            <Select
              label="Status"
              value={editForm.inspection_status}
              onChange={(val) => setEditForm({ ...editForm, inspection_status: val })}
            >
              <Option value="scheduled">Scheduled</Option>
              <Option value="in_progress">In Progress</Option>
              <Option value="completed">Completed</Option>
            </Select>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button 
            variant="text" 
            onClick={() => {
              setShowEditModal(false);
              setEditModalError('');
              setSelectedInspection(null);
            }} 
            className="mr-2 shadow-none"
          >
            Cancel
          </Button>
          <Button onClick={handleUpdateInspection} className="bg-brand-primary shadow-none rounded-none">
            Save Changes
          </Button>
        </DialogFooter>
      </Dialog>

      {/* Upload Link Modal */}
      <Dialog open={showUploadModal} handler={setShowUploadModal} className="m-2 sm:m-4">
        <DialogHeader>Upload {uploadLinkType === 'certificate' ? 'Certificate' : 'Report'} Link</DialogHeader>
        <DialogBody>
          {uploadModalError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {uploadModalError}
            </div>
          )}
          <p className="mb-4 text-gray-600">
            Please upload the {uploadLinkType === 'certificate' ? 'certificate' : 'report'} to Google Drive or your file storage service, then paste the link below.
          </p>
          
          <div className="mb-4">
            <Select
              label="Link Type"
              value={uploadLinkType}
              onChange={(val) => {
                setUploadLinkType(val);
                setUploadLink(val === 'certificate' 
                  ? (selectedInspection?.certificate_link || '')
                  : (selectedInspection?.inspection_report_link || '')
                );
                setIsReplacingLink(false);
              }}
            >
              <Option value="certificate">Certificate Link</Option>
              <Option value="report">Report Link</Option>
            </Select>
          </div>
          
          {selectedInspection && (
            <>
              {((uploadLinkType === 'certificate' && selectedInspection.certificate_link) || 
                (uploadLinkType === 'report' && selectedInspection.inspection_report_link)) && 
                !isReplacingLink && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current {uploadLinkType === 'certificate' ? 'Certificate' : 'Report'} Link:
                  </label>
                  <Input
                    type="url"
                    value={uploadLinkType === 'certificate' 
                      ? selectedInspection.certificate_link 
                      : selectedInspection.inspection_report_link}
                    readOnly
                    disabled
                    className="bg-gray-100"
                  />
                  {selectedInspection.inspection_status !== 'completed' && (
                    <Button
                      variant="outlined"
                      onClick={() => setIsReplacingLink(true)}
                      className="mt-2 text-sm"
                      size="sm"
                    >
                      Replace Link
                    </Button>
                  )}
                  {selectedInspection.inspection_status === 'completed' && (
                    <p className="mt-2 text-xs text-gray-500 italic">
                      This inspection is completed. The link cannot be changed.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
          
          {(isReplacingLink || 
            (uploadLinkType === 'certificate' && !selectedInspection?.certificate_link) ||
            (uploadLinkType === 'report' && !selectedInspection?.inspection_report_link)) && (
            <>
              <Input
                type="url"
                label={`${uploadLinkType === 'certificate' ? 'Certificate' : 'Report'} Link (Google Drive or file storage URL)`}
                placeholder="https://drive.google.com/file/d/..."
                value={uploadLink}
                onChange={(e) => {
                  setUploadLink(e.target.value);
                  setUploadModalError('');
                }}
                disabled={isVerifyingUrl}
              />
              {isVerifyingUrl && (
                <Typography className="text-sm text-blue-600 mt-2">
                  Verifying URL...
                </Typography>
              )}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="text" onClick={() => {
            setShowUploadModal(false);
            setUploadLink('');
            setSelectedInspection(null);
            setUploadModalError('');
            setIsReplacingLink(false);
            setIsVerifyingUrl(false);
          }} className="mr-2 shadow-none" disabled={isVerifyingUrl}>
            Cancel
          </Button>
          {(isReplacingLink || 
            (uploadLinkType === 'certificate' && !selectedInspection?.certificate_link) ||
            (uploadLinkType === 'report' && !selectedInspection?.inspection_report_link)) && (
            <Button 
              onClick={handleUploadLink} 
              className="bg-brand-primary shadow-none rounded-none"
              disabled={isVerifyingUrl}
            >
              {isVerifyingUrl ? 'Verifying...' : (isReplacingLink ? 'Update Link' : 'Save Link')}
            </Button>
          )}
        </DialogFooter>
      </Dialog>
    </div>
  );
};

export default GovernmentInspections;
